import { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from './store/useStore';
import { ToastContainer } from './components/Toast';
import { DamageNumberContainer } from './components/Effects';
import { GlobalStatus } from './components/GlobalStatus';
import PixiApp from './game/PixiApp';
import OrientationHint from './components/LandscapeGuard';
import OverlayShell from './components/OverlayShell';

import { LoadingSpinner } from './components/LoadingSpinner';

// Lazy load heavy/secondary pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const CreateCharacter = lazy(() => import('./pages/CreateCharacter'));
const Game = lazy(() => import('./pages/Game'));
const Battle = lazy(() => import('./pages/Battle'));
// DeckBuilder and Equipment are now rendered as overlays via OverlayShell
const ClassAdvance = lazy(() => import('./pages/ClassAdvance'));
const PvpArena = lazy(() => import('./pages/PvpArena'));
const Attendance = lazy(() => import('./pages/Attendance'));
const CardCodex = lazy(() => import('./pages/CardCodex'));
const MonsterCodex = lazy(() => import('./pages/MonsterCodex'));
const Profile = lazy(() => import('./pages/Profile'));
const Achievements = lazy(() => import('./pages/Achievements'));
const SkillTree = lazy(() => import('./pages/SkillTree'));
const Guild = lazy(() => import('./pages/Guild'));
const DungeonRanking = lazy(() => import('./pages/DungeonRanking'));
const Trade = lazy(() => import('./pages/Trade'));
// Settings is now rendered as an overlay via OverlayShell
const ThemeStore = lazy(() => import('./pages/ThemeStore'));
const BattlePass = lazy(() => import('./pages/BattlePass'));
const TierRewards = lazy(() => import('./pages/TierRewards'));

function Loading() {
  return <LoadingSpinner text="Loading RPG World..." />;
}

function ProtectedRoute({ children, allowTest = false }) {
  const { isLoggedIn } = useStore();
  const [searchParams] = useSearchParams();

  // 테스트 모드는 로그인 없이 접근 가능
  if (allowTest && searchParams.get('test')) {
    return children;
  }

  return isLoggedIn ? children : <Navigate to="/login" />;
}

// Export React Router navigate to window so Phaser scenes can do SPA navigation
// instead of window.location.href (which destroys the Phaser singleton).
function NavigateExporter() {
  const navigate = useNavigate();
  useEffect(() => {
    window.__tt_navigate = (path) => {
      try { navigate(path); } catch (_) { window.location.href = path; }
    };
    return () => { delete window.__tt_navigate; };
  }, [navigate]);
  return null;
}

// Lazy-load PartyFAB so it doesn't add to the initial bundle
const PartyFAB = lazy(() => import('./components/party/PartyFAB'));

/** Shows PartyFAB on all routes except battle/auth screens */
function PartyOverlay() {
  const location = useLocation();
  const isLoggedIn = useStore(s => s.isLoggedIn);
  const hideRoutes = ['/battle', '/login', '/register', '/create-character'];
  const show = isLoggedIn && !hideRoutes.includes(location.pathname);
  if (!show) return null;
  return (
    <Suspense fallback={null}>
      <PartyFAB />
    </Suspense>
  );
}

function App() {
  const { checkAuth, isLoggedIn } = useStore();

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <>
      <OrientationHint />
      <BrowserRouter>
        {/* Export React Router navigate to Phaser */}
        <NavigateExporter />
        {/* PixiJS engine: mounted once when logged in, never unmounted across routes */}
        {isLoggedIn && <PixiApp />}
        <ToastContainer />
        <DamageNumberContainer />
        <GlobalStatus />
        <PartyOverlay />
        <OverlayShell />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/login" element={isLoggedIn ? <Navigate to="/" /> : <Login />} />
            <Route path="/register" element={isLoggedIn ? <Navigate to="/" /> : <Register />} />
            <Route path="/create-character" element={
              <ProtectedRoute><CreateCharacter /></ProtectedRoute>
            } />
            <Route path="/battle" element={
              <ProtectedRoute allowTest><Battle /></ProtectedRoute>
            } />
            {/* /deck and /equipment are now overlays — see OverlayShell */}
            <Route path="/class" element={
              <ProtectedRoute><ClassAdvance /></ProtectedRoute>
            } />
            <Route path="/pvp" element={
              <ProtectedRoute><PvpArena /></ProtectedRoute>
            } />
            <Route path="/pvp/tier-rewards" element={
              <ProtectedRoute><TierRewards /></ProtectedRoute>
            } />
            <Route path="/attendance" element={
              <ProtectedRoute><Attendance /></ProtectedRoute>
            } />
            <Route path="/codex" element={<CardCodex />} />
            <Route path="/monster-codex" element={<MonsterCodex />} />
            <Route path="/profile" element={
              <ProtectedRoute><Profile /></ProtectedRoute>
            } />
            <Route path="/achievements" element={
              <ProtectedRoute><Achievements /></ProtectedRoute>
            } />
            <Route path="/skills" element={
              <ProtectedRoute><SkillTree /></ProtectedRoute>
            } />
            <Route path="/guild" element={
              <ProtectedRoute><Guild /></ProtectedRoute>
            } />
            <Route path="/ranking" element={
              <ProtectedRoute><DungeonRanking /></ProtectedRoute>
            } />
            <Route path="/trade" element={
              <ProtectedRoute><Trade /></ProtectedRoute>
            } />
            {/* /settings is now an overlay — see OverlayShell */}
            <Route path="/themes" element={
              <ProtectedRoute><ThemeStore /></ProtectedRoute>
            } />
            <Route path="/battlepass" element={
              <ProtectedRoute><BattlePass /></ProtectedRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute><Game /></ProtectedRoute>
            } />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;
